// libs
import React from 'react';
import addDays from 'date-fns/addDays';
import addYears from 'date-fns/addYears';
import isAfter from 'date-fns/isAfter';

import { TFunction } from '@lyearn/i18n';
import { Stack, Typography } from '@lyearn/ui';
// components
import { Fields, FieldType, useFormFieldValue } from '@/components/Form';
// styles
import {
  AssignmentDueDateEnum,
  AssignmentScheduleDateEnum,
  AssignmentStatusEnum,
  AssignmentType,
  DueDateAssignmentConfig,
  DueDateAssignmentConfigInput,
  EventSelection,
  NotificationConfigurationType,
  ScheduleDateAssignmentConfigInput,
  UserGroupSyncAssignmentConfigs,
  UserGroupSyncAssignmentConfigsInput,
} from '@/types/schema';

import { AssignmentCreatorInitialContentType } from '../../type';
import { adaptDueDatesOnEdit } from '../../utils';
import Assigner from '../Assigner';
import ContentAndCollectionList from '../ContentAndCollectionList';
import DueDate from '../DueDate';
import LearnersAndGroupList from '../LearnersAndGroupList';
import NotificationSettings from '../NotificationSettings';
import Settings from '../Settings';
import { AssignmentCreatorSectionKeys } from './types';

export const DEFAULT_DEACTIVATION_DURATION = 1; // in years
export const DEFAULT_DUE_DATE_CONFIG: DueDateAssignmentConfig = {
  dueDateType: AssignmentDueDateEnum.NoDueDate,
};
export const DEFAULT_USER_GROUP_SYNC_CONFIGS: UserGroupSyncAssignmentConfigs = {
  autoAssignGroupUsers: false,
};

export const getDefaultDeactivationDate = () =>
  addYears(new Date(), DEFAULT_DEACTIVATION_DURATION).toISOString();

export const getDisabledFields = (assignment?: AssignmentType, isDuplicating?: boolean) => {
  if (!assignment || isDuplicating) {
    return [];
  }

  switch (assignment.status) {
    case AssignmentStatusEnum.Draft:
      return [];
    case AssignmentStatusEnum.Scheduled:
      return ['scheduleDateConfig'];
    case AssignmentStatusEnum.Active:
    case AssignmentStatusEnum.InProgress:
      return [
        'scheduleDateConfig',
        'contentIds',
        'userIds',
        'userGroupIds',
        'collectionIds',
        'assignerId',
      ];
    case AssignmentStatusEnum.Archived:
    case AssignmentStatusEnum.Deleted:
    case AssignmentStatusEnum.Completed:
      return [
        'dueDateConfig',
        'scheduleDateConfig',
        'contentIds',
        'userIds',
        'userGroupIds',
        'collectionIds',
        'assignerId',
      ];
    default:
      return [];
  }
};

export const getInitialValues = (
  userId: string,
  assignment?: AssignmentType,
  isDuplicating?: boolean,
  initialContents?: AssignmentCreatorInitialContentType,
) => {
  if (!assignment) {
    return {
      contentIds: initialContents?.contentIds ?? [],
      userIds: initialContents?.assignedToUserIds ?? [],
      userGroupIds: [] as string[],
      collectionIds: initialContents?.collectionIds ?? [],
      userGroupSyncConfigs: {
        ...DEFAULT_USER_GROUP_SYNC_CONFIGS,
        deactivateFrom: getDefaultDeactivationDate(),
      },
      name: '',
      assignerId: userId,
      dueDateConfig: DEFAULT_DUE_DATE_CONFIG,
      scheduleDateConfig: {
        scheduleDateType: AssignmentScheduleDateEnum.NoScheduleDate,
      },
      assignmentItemsDueDateConfig: {},
      removedUserIds: [],
      importedUserGroupIds: [],
      notificationConfigurations: [] as NotificationConfigurationType[],
      eventSelections: initialContents?.eventSelections ?? ([] as EventSelection[]),
    };
  }

  return {
    contentIds: assignment.contentIds,
    userIds: assignment.userIds,
    userGroupIds: assignment.userGroupIds,
    collectionIds: assignment.collectionIds,
    userGroupSyncConfigs: assignment.userGroupSyncConfigs,
    name: isDuplicating
      ? `Copy of ${assignment.name} #${assignment.assignmentNumber}`
      : assignment.name,
    assignerId: isDuplicating ? userId : assignment.assignerId,
    dueDateConfig: assignment.dueDateConfig,
    scheduleDateConfig: assignment.scheduleDateConfig,
    assignmentItemsDueDateConfig: adaptDueDatesOnEdit(assignment.assignmentItemsDueDateConfig),
    notificationConfigurations: assignment.notificationConfigurations,
    eventSelections: assignment.eventSelections,
    removedUserIds: assignment.removedUserIds,
    importedUserGroupIds: assignment.importedUserGroupIds,
  };
};

export type AssignmentFormValues = ReturnType<typeof getInitialValues>;

export const getFormFields = (
  t: TFunction,
  disabledFields: string[],
): Fields<AssignmentFormValues> => ({
  contentIds: {
    name: 'contentIds',
    type: FieldType.SlowCustom,
    Component: ({ value: contentIds, setFieldValue }) => {
      const collectionIds = useFormFieldValue('collectionIds');
      const eventSelections = useFormFieldValue('eventSelections');
      const assignmentItemsDueDateConfig = useFormFieldValue('assignmentItemsDueDateConfig');

      return (
        <ContentAndCollectionList
          assignmentItemsDueDateConfig={assignmentItemsDueDateConfig}
          collectionIds={collectionIds}
          contentIds={contentIds}
          disabled={disabledFields.includes('contentIds')}
          eventSelections={eventSelections}
          setFieldValue={setFieldValue}
        />
      );
    },
  },
  userIds: {
    name: 'userIds',
    type: FieldType.SlowCustom,
    Component: ({ value: userIds, onChange: onChangeUserIds, setFieldValue }) => {
      const userGroupIds = useFormFieldValue('userGroupIds');
      const importedUserGroupIds = useFormFieldValue('importedUserGroupIds');
      const removedUserIds = useFormFieldValue('removedUserIds');
      return (
        <LearnersAndGroupList
          disabled={disabledFields.includes('userIds')}
          importedUserGroupIds={importedUserGroupIds}
          learnerIds={userIds}
          removedUserIds={removedUserIds}
          userGroupIds={userGroupIds}
          onChange={({ userIds, userGroupIds, importedUserGroupIds, removedUserIds }) => {
            userIds && onChangeUserIds(userIds);
            userGroupIds && setFieldValue('userGroupIds', userGroupIds, true);
            importedUserGroupIds &&
              setFieldValue('importedUserGroupIds', importedUserGroupIds, true);
            removedUserIds && setFieldValue('removedUserIds', removedUserIds, true);
          }}
        />
      );
    },
  },
  userGroupSyncConfigs: {
    name: 'userGroupSyncConfigs',
    type: FieldType.SlowCustom,
    Component: ({ value, onChange }) => {
      return (
        <Settings
          className="ps-8"
          disabled={disabledFields.includes('userGroupSyncConfigs')}
          userGroupSyncConfigs={value}
          onChange={onChange}
        />
      );
    },
  },
  notificationConfigurations: {
    name: 'notificationConfigurations',
    type: FieldType.Custom,
    Component: ({ value, onChange }) => {
      return (
        <NotificationSettings
          className="ps-8"
          notificationConfigurations={value ?? undefined}
          onChange={onChange}
        />
      );
    },
  },
  dueDateConfig: {
    name: 'dueDateConfig',
    type: FieldType.SlowCustom,
    Component: ({ value, onChange }) => {
      return (
        <Stack className="px-8" direction="col">
          <Typography className="mb-8" variant="label-01">
            {t('AssignmentCreator:DueDate')}
          </Typography>
          <DueDate
            disabled={disabledFields.includes('dueDateConfig')}
            value={value}
            onChange={onChange}
          />
        </Stack>
      );
    },
  },
  name: {
    name: 'name',
    type: FieldType.Text,
    label: t('Common:Form.Title'),
    required: false,
    size: 'large',
    placeholder: t('AssignmentCreator:AssignmentNamePlaceholder'),
  },
  assignerId: {
    name: 'assignerId',
    label: t('AssignmentCreator:AssignedBy'),
    type: FieldType.SlowCustom,
    Component: ({ value, onChange }) => {
      return (
        <Assigner
          assignerId={value}
          disabled={disabledFields.includes('assignerId')}
          onChange={onChange as TSFixMe}
        />
      );
    },
  },
});

export const getSectionLayout = (t: TFunction, canConfigureAssignmentNotification?: boolean) => [
  {
    id: AssignmentCreatorSectionKeys.Content,
    layout: ['contentIds'],
  },
  {
    id: AssignmentCreatorSectionKeys.Learners,
    layout: ['userIds'],
  },
  {
    id: AssignmentCreatorSectionKeys.Settings,
    layout: canConfigureAssignmentNotification
      ? ['userGroupSyncConfigs', 'notificationConfigurations', 'dueDateConfig']
      : ['userGroupSyncConfigs', 'dueDateConfig'],
    name: t('Common:Actions.Settings'),
  },
  {
    id: AssignmentCreatorSectionKeys.Details,
    layout: ['name', 'assignerId'],
    name: t('AssignmentCreator:Details'),
  },
];

export const isSchduledDateError = (
  scheduleDateConfig: ScheduleDateAssignmentConfigInput,
  dueDateConfig: DueDateAssignmentConfigInput,
) => {
  if (
    dueDateConfig.dueDateType === AssignmentDueDateEnum.Fixed &&
    scheduleDateConfig?.scheduleDate &&
    dueDateConfig?.dueDate
  ) {
    if (isAfter(new Date(scheduleDateConfig.scheduleDate), new Date(dueDateConfig.dueDate))) {
      return true;
    }
  }

  if (
    dueDateConfig.dueDateType === AssignmentDueDateEnum.Fixed &&
    scheduleDateConfig?.scheduleDateType === AssignmentScheduleDateEnum.Dynamic &&
    dueDateConfig?.dueDate &&
    scheduleDateConfig?.value
  ) {
    if (isAfter(addDays(new Date(), scheduleDateConfig.value), new Date(dueDateConfig.dueDate))) {
      return true;
    }
  }

  return false;
};

export const isDueDateError = (dueDateConfig: DueDateAssignmentConfigInput) => {
  if (dueDateConfig.dueDateType === AssignmentDueDateEnum.Fixed && dueDateConfig?.dueDate) {
    if (isAfter(new Date(), new Date(dueDateConfig.dueDate))) {
      return true;
    }
  }

  return false;
};

export const isDueDateEmpty = (dueDateConfig: DueDateAssignmentConfigInput) => {
  if (dueDateConfig.dueDateType === AssignmentDueDateEnum.Dynamic && !dueDateConfig.value) {
    return true;
  }

  return (
    dueDateConfig.dueDateType === AssignmentDueDateEnum.Relative &&
    typeof dueDateConfig.value !== 'number'
  );
};

export const isTagEmpty = (dueDateConfig: DueDateAssignmentConfigInput) => {
  return dueDateConfig.dueDateType === AssignmentDueDateEnum.Relative && !dueDateConfig.tagId;
};

export const isDeactivateDateError = (
  userGroupSyncConfigs: UserGroupSyncAssignmentConfigsInput,
) => {
  if (!userGroupSyncConfigs.autoAssignGroupUsers) {
    return false;
  }
  if (userGroupSyncConfigs?.deactivateFrom && userGroupSyncConfigs?.deactivateFrom !== '-1') {
    if (isAfter(Date.now(), new Date(userGroupSyncConfigs?.deactivateFrom))) {
      return true;
    }
  }

  return false;
};

export const validateForm = (t: TFunction) => (values: AssignmentFormValues) => {
  const errors: any = {};
  const {
    contentIds,
    userIds,
    userGroupIds,
    collectionIds,
    dueDateConfig,
    userGroupSyncConfigs,
    eventSelections,
    importedUserGroupIds,
  } = values;

  if (!contentIds.length && !collectionIds.length && !eventSelections?.length) {
    errors.contentIds = t('AssignmentCreator:ContentError');
  }

  if (!userIds.length && !userGroupIds.length && !importedUserGroupIds?.length) {
    errors.userIds = t('AssignmentCreator:LearnersError');
  }

  // if (isSchduledDateError(scheduleDateConfig, dueDateConfig)) {
  //   errors.scheduleDateConfig = t('AssignmentCreator:ScheduleDateError');
  // }

  if (isDueDateEmpty(dueDateConfig)) {
    errors.dueDateConfig = t('AssignmentCreator:DueDateEmptyError');
  } else if (isDueDateError(dueDateConfig)) {
    errors.dueDateConfig = t('AssignmentCreator:DueDateError');
  } else if (isTagEmpty(dueDateConfig)) {
    errors.dueDateConfig = t('AssignmentCreator:TagError');
  }

  if (isDeactivateDateError(userGroupSyncConfigs)) {
    errors.userGroupSyncConfigs = t('AssignmentCreator:AutoAssignError');
  }

  return errors;
};
